import React, { useEffect, useState, useCallback } from "react";
import { Loader2, MapPin, Wrench, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import API, { formatApiError } from "../../lib/api";
import { SERVICE_TYPES, URGENCY_META, labelOf } from "../../lib/gmaoConstants";
import { Card } from "../ui/card";
import StatusBadge from "./StatusBadge";
import TechnicianInterventionView from "./TechnicianInterventionView";

const TechnicianDashboard = () => {
  const [interventions, setInterventions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await API.get("/interventions", { params: { limit: 200 } });
      setInterventions(data.interventions);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handleChanged = (updated) => {
    setInterventions((prev) => prev.map((i) => (i.id === updated.id ? updated : i)));
    setSelected((s) => (s && s.id === updated.id ? updated : s));
  };

  if (selected) {
    return (
      <TechnicianInterventionView
        intervention={selected}
        onBack={() => setSelected(null)}
        onChanged={handleChanged}
      />
    );
  }

  return (
    <div data-testid="technician-dashboard">
      <div className="mb-5">
        <h1 className="text-2xl font-bold text-slate-800">Mes interventions</h1>
        <p className="text-sm text-muted-foreground">Vos missions affectées</p>
      </div>

      {loading ? (
        <div className="p-10 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-[hsl(207,78%,42%)]" /></div>
      ) : interventions.length === 0 ? (
        <Card className="p-12 text-center">
          <Wrench className="w-10 h-10 mx-auto text-slate-300 mb-3" />
          <p className="text-muted-foreground">Aucune intervention affectée pour le moment.</p>
        </Card>
      ) : (
        <div className="space-y-3 max-w-xl mx-auto">
          {interventions.map((i) => {
            const u = URGENCY_META[i.urgency] || {};
            return (
              <Card
                key={i.id}
                className="p-4 cursor-pointer hover:shadow-md transition-shadow active:bg-slate-50"
                onClick={() => setSelected(i)}
                data-testid={`tech-card-${i.dossierNumber}`}
              >
                <div className="flex items-center justify-between mb-2">
                  <span className="font-mono text-sm font-semibold text-[hsl(207,78%,42%)]">{i.dossierNumber}</span>
                  <div className="flex items-center gap-2">
                    <span className={`text-[10px] px-2 py-0.5 rounded ${u.color || ""}`}>{u.label || i.urgency}</span>
                    <StatusBadge status={i.status} />
                  </div>
                </div>
                <p className="text-sm font-medium text-slate-800">{labelOf(SERVICE_TYPES, i.serviceType)}</p>
                {i.clientName && <p className="text-xs text-slate-500">{i.clientName}</p>}
                <div className="flex items-center justify-between mt-2">
                  <div className="flex items-start gap-1 text-xs text-muted-foreground">
                    <MapPin className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
                    <span>{i.address} {i.postalCode} {i.city}</span>
                  </div>
                  <ChevronRight className="w-4 h-4 text-slate-400 flex-shrink-0" />
                </div>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default TechnicianDashboard;
